import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  Basket,
  BasketItem,
  CreateBasketItem,
  KnownApplicantFulfillmentMethod,
} from '@modules/checkout-store';
import { determinePrice } from '@modules/checkout-store/effects/util/format-basket-item';
import { CoreFacade } from '@modules/core/core.facade';
import { ENVIRONMENT } from '@modules/core';
import { formatOrderDate_DatePart } from '@modules/order-store/util';
import { RouteNames } from '@nx-ng/constants';
import {
  GTM_ROUTE_TO_EVENT_DETAILS,
  LOGIN_EVENT,
} from '@nx-ng/constants/gtm-route-map';
import {
  Environment,
  GtmBasketEventName,
  GtmDeliveryType,
  GtmEvent,
  GtmEventEcommerceItem,
  GtmEventName,
  GtmEventUser,
  GtmRouteDetails,
  GtmSiteDetails,
  IOrderTicket,
  ITicketProductWithBundleType,
} from '@nx-ng/types';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { Order } from '@modules/order-store/client/src';

@Injectable({
  providedIn: 'root',
})
export class GtmEventService {
  private gtmEnabled = false;

  constructor(
    @Inject(ENVIRONMENT) private env: Environment,
    private router: Router,
    private gtm: GoogleTagManagerService,
    private coreFacade: CoreFacade
  ) {
    this.coreFacade.gtmEnabled$.subscribe((enabled) => {
      this.gtmEnabled = enabled;
    });
  }

  getSiteDetails(): GtmSiteDetails {
    return {
      environment: this.env.name,
      versionNo: 'v1.0',
    };
  }

  sendLoginEvent() {
    if (!this.gtmEnabled) {
      return;
    }

    const evt: GtmEvent = {
      ...LOGIN_EVENT,
      page: {
        ...LOGIN_EVENT.page,
        url: this.env.b2c.authority,
      },
      event: LOGIN_EVENT.event as GtmEventName,
      user: {
        loginMethod: 'email',
        loginState: false,
      },
      site: this.getSiteDetails(),
    };

    this.gtm.pushTag(evt);
  }

  sendPageChangeEvent(userDetails: GtmEventUser) {
    if (!this.gtmEnabled) {
      return;
    }
    const eventDetails = this.getEventDetails();

    if (!eventDetails || eventDetails.event === undefined) {
      return;
    }
    const gtmTag: GtmEvent = this.formatChangePageEvent({
      eventDetails,
      userDetails,
    });

    this.gtm.pushTag(gtmTag);
  }

  private getEventDetails(): GtmRouteDetails | undefined {
    return GTM_ROUTE_TO_EVENT_DETAILS[
      this.router.url.substring(1) as RouteNames
    ];
  }

  formatChangePageEvent({
    eventDetails,
    userDetails,
  }: {
    eventDetails: GtmRouteDetails;
    userDetails: GtmEventUser;
  }): GtmEvent {
    return {
      ...eventDetails,
      event: eventDetails.event as GtmEventName,
      user: {
        ...userDetails,
      },
      site: this.getSiteDetails(),
    };
  }

  sendBasketEvent({
    basketItem,
    allTickets,
    basket,
    userDetails,
  }: {
    basketItem: CreateBasketItem;
    allTickets: ITicketProductWithBundleType[];
    basket: Basket | undefined;
    userDetails: GtmEventUser;
  }) {
    if (!this.gtmEnabled) {
      return;
    }

    const basketEventName: GtmBasketEventName = this.determineBasketEvent({
      basket,
      item: basketItem,
    });

    const gtmEcommerceItem: GtmEventEcommerceItem | undefined =
      this.formatBasketEventItem({
        basketItem,
        allTickets,
        currency: basket?.currency || 'GBP',
      });

    if (!gtmEcommerceItem) {
      return;
    }

    const eventDetails = this.getEventDetails();

    if (!eventDetails) {
      return;
    }

    const gtmTag = this.formatBasketEvent({
      eventDetails,
      userDetails,
      gtmEcommerceItem,
      basketEventName,
    });
    this.gtm.pushTag(gtmTag);
  }

  determineBasketEvent({
    basket,
    item,
  }: {
    basket: Basket | undefined;
    item: CreateBasketItem;
  }): GtmBasketEventName {
    if (!basket) {
      return 'addToBasket';
    }

    const foundItemInBasket = basket.basketItems.find(
      (basketItem: BasketItem) => basketItem.ticketID === item.ticketID
    );
    if (!foundItemInBasket) {
      return 'addToBasket';
    }

    return foundItemInBasket.quantity > item.quantity
      ? 'removeFromBasket'
      : 'addToBasket';
  }

  buildGtmEventEcommerceItem({
    findTicketDetails,
    delivery,
    currency,
    quantity,
  }: {
    findTicketDetails: ITicketProductWithBundleType | undefined;
    delivery: GtmDeliveryType | undefined;
    currency: string;
    quantity: number;
  }) {
    if (!findTicketDetails) {
      return undefined;
    }
    const price = determinePrice(findTicketDetails.price);

    return {
      item_name: findTicketDetails.description || '',
      item_id: findTicketDetails.id?.toString() || '0',
      delivery: delivery,
      price: price,
      discounted_price: price,
      item_brand: 'NX',
      item_category: this.formatCategory1Text(findTicketDetails),
      item_category2: findTicketDetails.ticketArea?.travelArea || '',
      item_category3: findTicketDetails.passengerType?.description || '',
      item_category4: this.formatDurationText(findTicketDetails),
      item_category5: findTicketDetails?.ticketTime?.name || '',
      quantity: quantity,
      currency: currency,
      discount: 0,
    };
  }

  formatBasketEventItem({
    basketItem,
    allTickets,
    delivery = undefined,
    currency,
  }: {
    basketItem: CreateBasketItem;
    allTickets: ITicketProductWithBundleType[];
    delivery?: GtmDeliveryType | undefined;
    currency: string;
  }): GtmEventEcommerceItem | undefined {
    const findTicketDetails = allTickets.find(
      (ticket: ITicketProductWithBundleType) =>
        ticket.id === basketItem.ticketID
    );

    return this.buildGtmEventEcommerceItem({
      findTicketDetails,
      delivery,
      currency,
      quantity: basketItem.quantity < 1 ? 1 : basketItem.quantity, // remove basket events should always send qty of 1
    });
  }

  formatOrderEventItem({
    orderTicket,
    allTickets,
    delivery = undefined,
    currency,
  }: {
    orderTicket: IOrderTicket;
    allTickets: ITicketProductWithBundleType[];
    delivery?: GtmDeliveryType | undefined;
    currency: string;
  }): GtmEventEcommerceItem | undefined {
    const findTicketDetails = allTickets.find(
      (ticket: ITicketProductWithBundleType) =>
        ticket.id?.toString() == orderTicket.id
    );

    return this.buildGtmEventEcommerceItem({
      findTicketDetails,
      delivery,
      currency,
      quantity: orderTicket.quantity,
    });
  }

  formatDurationText(ticket: ITicketProductWithBundleType): string {
    if (!ticket.price || ticket.price.length === 0) {
      return '';
    }

    if (
      ticket.bundleType === 'flexible' ||
      ticket.bundleType === 'payAsYouGo'
    ) {
      return 'flexifare';
    }

    if (ticket.termDates) {
      return `${formatOrderDate_DatePart(
        ticket.termDates.termDateFrom
      )} - ${formatOrderDate_DatePart(ticket.termDates.termDateTo)}`;
    }

    if (ticket.isDirectDebit) {
      return 'Monthly subscription';
    }

    return ticket.noOfDays ? this.formatAsNumberOfWeeks(ticket.noOfDays) : '';
  }

  formatCategory1Text(ticket: ITicketProductWithBundleType): string {
    if (
      ticket.bundleType === 'flexible' ||
      ticket.bundleType === 'payAsYouGo'
    ) {
      return 'flexifare';
    }

    if (ticket.isDirectDebit) {
      return 'subscription';
    }

    return 'standard';
  }

  formatAsNumberOfWeeks(noOfDays: number): string {
    const weeks = Math.floor(noOfDays / 7);
    return `${weeks} week`;
  }

  formatBasketEvent({
    userDetails,
    basketEventName,
    eventDetails,
    gtmEcommerceItem,
  }: {
    userDetails: GtmEventUser;
    basketEventName: GtmBasketEventName;
    eventDetails: GtmRouteDetails;
    gtmEcommerceItem: GtmEventEcommerceItem;
  }): GtmEvent {
    return {
      event: basketEventName,
      page: eventDetails.page,
      user: {
        ...userDetails,
      },
      ecommerce: {
        items: [gtmEcommerceItem],
      },
      site: this.getSiteDetails(),
    };
  }

  sendCheckoutEvent({
    allTickets = [],
    orderTickets = [],
    order,
    userDetails,
  }: {
    allTickets: ITicketProductWithBundleType[];
    orderTickets: IOrderTicket[];
    order: Order | undefined;
    userDetails: GtmEventUser;
  }) {
    if (!orderTickets || !order) {
      return;
    }

    const eventDetails =
      GTM_ROUTE_TO_EVENT_DETAILS[RouteNames.BOOKING_CONFIRMATION];

    if (!eventDetails) {
      return;
    }

    const gtmTag = this.formatCheckoutEvent({
      allTickets,
      order,
      orderTickets,
      userDetails,
      eventDetails,
    });

    this.gtm.pushTag(gtmTag);
  }

  private formatOrderItems({
    allTickets = [],
    orderTickets = [],
  }: {
    allTickets: ITicketProductWithBundleType[];
    orderTickets: IOrderTicket[];
  }): GtmEventEcommerceItem[] {
    return orderTickets.reduce(
      (agg: GtmEventEcommerceItem[], orderTicket: IOrderTicket) => {
        const newItem = this.formatOrderEventItem({
          orderTicket,
          allTickets,
          currency: 'GBP',
          delivery:
            orderTicket.fullfillmentMethod ===
            KnownApplicantFulfillmentMethod.Smartcard
              ? 'swift'
              : 'mTicket',
        });

        if (!newItem) {
          return agg;
        }
        return [...agg, newItem];
      },
      []
    );
  }

  determinePaymentMethods(
    basketItemsWithDelivery: { isDirectDebit: boolean }[]
  ): string {
    return basketItemsWithDelivery.some((item) => item.isDirectDebit)
      ? 'card, direct debit'
      : 'card';
  }

  formatCheckoutEvent({
    userDetails,
    eventDetails,
    allTickets = [],
    order,
    orderTickets,
  }: {
    userDetails: GtmEventUser;
    eventDetails: GtmRouteDetails;
    allTickets: ITicketProductWithBundleType[];
    order: Order;
    orderTickets: IOrderTicket[];
  }): GtmEvent {
    return {
      ...eventDetails,
      event: 'ticketReceipt',
      user: {
        ...userDetails,
        id: order.customer?.id || '',
      },
      ecommerce: {
        value: order.orderTotal,
        discounted_value: order.orderTotal,
        currency: 'GBP',
        paymentType: this.determinePaymentMethods(orderTickets),
        total_discount: 0,
        items: this.formatOrderItems({
          allTickets,
          orderTickets,
        }),
      },
      site: this.getSiteDetails(),
    };
  }
}
